import { useCallback, useEffect, useState } from 'react';
import Script from 'next/script';

import { SummaryGrid } from '~/components/modules/SummaryTable/SummaryGrid';
import { SummaryItem } from '~/components/modules/SummaryTable/SummaryItem';
import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { dispatchError } from '~/containers/ErrorContainer/errorEventListener';
import { SentryHelper } from '~/helpers/Sentry/SentryHelper';
import { useBitpayDeposit } from '~/hooks/payments/useBitpayDeposit';
import { useTracking } from '~/hooks/tracking/useTracking';
import { ModalMultiStepProps } from '~/models/deposits/ModalMultiStepProps';
import { PortfolioTypes } from '~/models/PortfolioType';
import { Button } from '~/vinovest-design-system/components';
import { Alert } from '~/vinovest-design-system/components/Form/components/Alert';

import { PortfolioDestination } from './PortfolioDestination';

interface CryptocurrencyTransferProps extends ModalMultiStepProps {
    onPaymentLinked: () => void;
}

/** Transfer funds using cryptocurrency through Bitpay.  */
export const CryptocurrencyTransfer = ({ onPaymentLinked, setStep }: CryptocurrencyTransferProps) => {
    const [destination, setDestination] = useState<PortfolioTypes>(PortfolioTypes.managed);
    const { loading, openBitpayPopup, bitpayComplete, error } = useBitpayDeposit(destination);
    const { captureEvent } = useTracking();

    const [depositAmount, setDepositAmount] = useState(5000);

    const onSelectBitpay = useCallback(async () => {
        captureEvent(AnalyticsEventNames.CryptocurrencySelectContinue, { portfolio: destination });

        try {
            captureEvent(AnalyticsEventNames.BeginBitpayPayment);
            await openBitpayPopup(depositAmount);
            if (bitpayComplete) {
                onPaymentLinked();
            }
        } catch (bitpayModalError) {
            SentryHelper.captureException(bitpayModalError);
            return dispatchError(bitpayModalError);
        }
    }, [openBitpayPopup, bitpayComplete, onPaymentLinked]);

    useEffect(() => {
        if (bitpayComplete) {
            captureEvent(AnalyticsEventNames.CompletedBitpayPayment);
            onPaymentLinked();
        }
    }, [bitpayComplete]);

    return (
        <>
            <SummaryGrid>
                <SummaryItem label={'Deposit to'}>
                    <PortfolioDestination destination={destination} setDestination={setDestination} />
                </SummaryItem>
                <form className={'py-3 px-4 sm:px-6'}>
                    <div className={'flex items-center'}>
                        <label className={'uppercase mr-10 text-10'} htmlFor={'amount'}>
                            Amount
                        </label>
                        <input
                            type={'number'}
                            name={'amount'}
                            placeholder={'5000'}
                            value={depositAmount}
                            onChange={(e) => setDepositAmount(e.target.valueAsNumber)}
                            className={'border border-neutrals-200 rounded py-3 px-4 flex-1 text-12'}
                        />
                    </div>

                    <p className={'text-12 text-neutrals-500 mt-8'}>
                        Due to the technical nature of cryptocurrency payments, you need to provide a deposit amount
                        first. Then we will initiate BitPay so you can complete your deposit
                    </p>
                    {error && (
                        <Alert classnames={'mt-5'} type={'error'}>
                            {error}
                        </Alert>
                    )}
                    <div className={'flex flex-col pt-5'}>
                        <Button
                            type={'submit'}
                            variant={'green'}
                            classnames={'mb-5'}
                            onClick={onSelectBitpay}
                            disabled={loading}
                        >
                            Continue
                        </Button>
                        <Button variant={'secondary'} onClick={() => setStep(0)}>
                            Back
                        </Button>
                    </div>
                </form>
            </SummaryGrid>
            <Script src={'https://bitpay.com/bitpay.min.js'} strategy={'lazyOnload'} />
        </>
    );
};
